import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const REGISTER_URL = "https://forms.gle/EmfMDZAH7bggopD97";

const theme = {
  background: "#fafafa",
  text: "#333",
  textSecondary: "#555",
  textTertiary: "#666",
  textLight: "#888",
  nameColor: "#1a1a1a",
  divider: "#333",
  iconColor: "#333",
};

const styles = {
  page: {
    minHeight: "100vh",
    width: "100%",
    backgroundColor: theme.background,
    color: theme.text,
    fontFamily: "'Georgia', serif",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "80px 24px 96px",
    boxSizing: "border-box",
  },
  content: {
    width: "100%",
    maxWidth: "620px",
    textAlign: "center",
  },
  overline: {
    fontSize: "0.85rem",
    fontWeight: "300",
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: theme.textLight,
    marginBottom: "1rem",
  },
  title: {
    fontSize: "2.6rem",
    fontWeight: "300",
    letterSpacing: "0.03em",
    color: theme.nameColor,
    lineHeight: "1.2",
    margin: "0 0 1.25rem 0",
  },
  subtitle: {
    fontSize: "1.05rem",
    lineHeight: "1.7",
    color: theme.textSecondary,
    fontWeight: "300",
    margin: "0 auto 2rem",
    maxWidth: "520px",
  },
  divider: {
    width: "60px",
    height: "1px",
    backgroundColor: theme.divider,
    margin: "2rem auto",
  },
  body: {
    fontSize: "1rem",
    lineHeight: "1.8",
    color: theme.textSecondary,
    fontWeight: "300",
    margin: "0 auto 2.5rem",
    maxWidth: "520px",
    textAlign: "left",
  },
  sessionsHeading: {
    fontSize: "0.85rem",
    fontWeight: "300",
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: theme.textLight,
    marginBottom: "1rem",
  },
  sessionsList: {
    listStyle: "none",
    padding: 0,
    margin: "0 auto 2.5rem",
    maxWidth: "520px",
    width: "100%",
  },
  sessionItem: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    padding: "16px 0",
    borderBottom: `1px solid ${theme.divider}`,
    textAlign: "left",
  },
  sessionHeader: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
  },
  sessionLabel: {
    fontSize: "0.72rem",
    fontWeight: "600",
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: theme.textLight,
    margin: 0,
  },
  sessionWhen: {
    fontSize: "0.95rem",
    fontWeight: "400",
    color: theme.nameColor,
    margin: 0,
  },
  sessionTopic: {
    fontSize: "0.92rem",
    lineHeight: "1.6",
    fontWeight: "300",
    color: theme.textSecondary,
    margin: 0,
  },
  cta: {
    display: "inline-block",
    padding: "14px 32px",
    borderRadius: "2px",
    border: `1px solid ${theme.divider}`,
    color: theme.nameColor,
    backgroundColor: "transparent",
    fontFamily: "'Georgia', serif",
    fontSize: "0.95rem",
    fontWeight: "400",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    textDecoration: "none",
    cursor: "pointer",
    transition:
      "background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease",
  },
  ctaNote: {
    fontSize: "0.85rem",
    lineHeight: "1.6",
    color: theme.textLight,
    fontWeight: "300",
    margin: "1rem auto 0",
    maxWidth: "440px",
  },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(26, 26, 26, 0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    zIndex: 1000,
    animation: "baxi-fade-in 0.18s ease-out",
  },
  modal: {
    backgroundColor: theme.background,
    color: theme.text,
    fontFamily: "'Georgia', serif",
    maxWidth: "440px",
    width: "100%",
    padding: "36px 32px 28px",
    borderRadius: "4px",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.18)",
    textAlign: "center",
    boxSizing: "border-box",
  },
  modalOverline: {
    fontSize: "0.78rem",
    fontWeight: "300",
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: theme.textLight,
    margin: "0 0 0.75rem 0",
  },
  modalTitle: {
    fontSize: "1.35rem",
    fontWeight: "400",
    color: theme.nameColor,
    margin: "0 0 1rem 0",
    letterSpacing: "0.02em",
  },
  modalBody: {
    fontSize: "0.98rem",
    lineHeight: "1.7",
    color: theme.textSecondary,
    fontWeight: "300",
    margin: "0 0 1.75rem 0",
  },
  modalActions: {
    display: "flex",
    gap: "10px",
    justifyContent: "center",
    flexWrap: "wrap",
  },
  modalPrimary: {
    display: "inline-block",
    padding: "12px 24px",
    borderRadius: "2px",
    border: `1px solid ${theme.divider}`,
    color: theme.background,
    backgroundColor: theme.nameColor,
    fontFamily: "'Georgia', serif",
    fontSize: "0.85rem",
    fontWeight: "400",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    textDecoration: "none",
    cursor: "pointer",
    transition:
      "background-color 0.25s ease, color 0.25s ease, border-color 0.25s ease",
  },
  modalSecondary: {
    display: "inline-block",
    padding: "12px 20px",
    borderRadius: "2px",
    border: "1px solid transparent",
    color: theme.textLight,
    backgroundColor: "transparent",
    fontFamily: "'Georgia', serif",
    fontSize: "0.85rem",
    fontWeight: "400",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    cursor: "pointer",
    transition: "color 0.2s ease",
  },
  backLink: {
    marginTop: "3rem",
    fontSize: "0.85rem",
    color: theme.textLight,
    textDecoration: "none",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    fontWeight: "300",
    borderBottom: `1px solid ${theme.textLight}`,
    paddingBottom: "2px",
    transition: "color 0.2s ease, border-color 0.2s ease",
  },
};

function BaxiTraining() {
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    const html = document.documentElement;
    const prevSnap = html.style.scrollSnapType;
    html.style.scrollSnapType = "none";
    const prevTitle = document.title;
    document.title = "Product Analytics Training — Baxi | Daniel Sumah";
    return () => {
      html.style.scrollSnapType = prevSnap;
      document.title = prevTitle;
    };
  }, []);

  useEffect(() => {
    if (!showConfirm) return;
    const onKey = (e) => {
      if (e.key === "Escape") setShowConfirm(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showConfirm]);

  const handleContinueToForm = () => {
    window.open(REGISTER_URL, "_blank", "noopener,noreferrer");
    setShowConfirm(false);
  };

  return (
    <div style={styles.page}>
      <div style={styles.content}>
        <p style={styles.overline}>Training</p>
        <h1 style={styles.title}>Product Analytics with PostHog</h1>
        <p style={styles.subtitle}>
          A hands-on training for Product Managers at Baxi on building a
          measurable, defensible product practice with PostHog.
        </p>

        <div style={styles.divider}></div>

        <p style={styles.body}>
          Across two sittings, we&apos;ll cover why product analytics matters,
          how to instrument the events that count, and how to work with
          PostHog&apos;s insights — trends, funnels, retention, and more — to
          turn signal into decisions you can stand behind.
        </p>

        <p style={styles.sessionsHeading}>Sessions</p>
        <ul style={styles.sessionsList}>
          <li style={styles.sessionItem}>
            <div style={styles.sessionHeader}>
              <p style={styles.sessionLabel}>Day 1</p>
              <p style={styles.sessionWhen}>
                Wednesday, April 29 &middot; 12:00 noon &ndash; 1:00 pm
              </p>
            </div>
            <p style={styles.sessionTopic}>
              Fundamentals of product analytics — what it is, why it matters,
              and how to think about it as a PM.
            </p>
          </li>
          <li style={styles.sessionItem}>
            <div style={styles.sessionHeader}>
              <p style={styles.sessionLabel}>Day 2</p>
              <p style={styles.sessionWhen}>
                Early May &middot; date to be confirmed
              </p>
            </div>
            <p style={styles.sessionTopic}>
              Hands-on with PostHog — instrumenting events, working with
              insights, and turning signal into decisions.
            </p>
          </li>
        </ul>

        <button
          type="button"
          onClick={() => setShowConfirm(true)}
          style={styles.cta}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = theme.iconColor;
            e.currentTarget.style.color = theme.background;
            e.currentTarget.style.borderColor = theme.iconColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
            e.currentTarget.style.color = theme.nameColor;
            e.currentTarget.style.borderColor = theme.divider;
          }}
        >
          Register
        </button>
        <p style={styles.ctaNote}>
          Registration takes under a minute. Joining details will be sent
          ahead of the session.
        </p>

        <div>
          <Link
            to="/"
            style={styles.backLink}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = theme.nameColor;
              e.currentTarget.style.borderColor = theme.nameColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = theme.textLight;
              e.currentTarget.style.borderColor = theme.textLight;
            }}
          >
            ← Back to home
          </Link>
        </div>
      </div>

      {showConfirm && (
        <div
          style={styles.modalOverlay}
          onClick={() => setShowConfirm(false)}
          role="presentation"
        >
          <div
            style={styles.modal}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="baxi-register-title"
          >
            <p style={styles.modalOverline}>Before you continue</p>
            <h2 id="baxi-register-title" style={styles.modalTitle}>
              Please register with your Baxi work email
            </h2>
            <p style={styles.modalBody}>
              This training was built specifically for Product Managers at
              Baxi, so please register with your Baxi work email. Invites to
              both sessions will be sent to that address.
            </p>
            <div style={styles.modalActions}>
              <button
                type="button"
                onClick={handleContinueToForm}
                style={styles.modalPrimary}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = theme.text;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = theme.nameColor;
                }}
              >
                Continue to form
              </button>
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                style={styles.modalSecondary}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = theme.nameColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = theme.textLight;
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BaxiTraining;
